import { useCallback, useEffect, useMemo, useState } from "react";
import type { InvestmentPack } from "../types/api";
import * as packService from "../services/investmentPackService";
import type {
  CreatePackPayload,
  UpdatePackPayload,
} from "../services/investmentPackService";
import { useToast } from "../components/ui/use-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DataTable, Pagination, type DataTableColumn } from "../components/table/DataTable";
import { resolveAssetUrl } from "../utils/url";
import { useTranslation } from "react-i18next";
import { pickPackName } from "../utils/localeDisplay";
import PackFormModal from "../components/admin/PackFormModal";

const AdminInvestmentPacksPage = () => {
  const { toast } = useToast();
  const [packs, setPacks] = useState<InvestmentPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPack, setSelectedPack] = useState<InvestmentPack | null>(null);
  const [saving, setSaving] = useState(false);
  const { t, i18n } = useTranslation();
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [totalPacks, setTotalPacks] = useState(0);
  const packsPerPage = 20;
  const totalPages = Math.max(1, Math.ceil(totalPacks / packsPerPage));

  const fetchPacks = useCallback(async () => {
    try {
      setLoading(true);
      const offset = (page - 1) * packsPerPage;
      const data = await packService.listPacks({ 
        limit: packsPerPage, 
        offset 
      });
      setPacks(data);
      // TODO: Le backend devrait retourner le total pour la pagination
      // Pour l'instant, on suppose qu'il y a plus de packs si on reçoit exactement packsPerPage
      if (data.length === packsPerPage) {
        setTotalPacks(page * packsPerPage + 1); // Estimation
      } else {
        setTotalPacks((page - 1) * packsPerPage + data.length);
      }
    } catch (error) {
      toast({
        title: t("admin.packs.errorTitle"),
        description:
          typeof error === "string"
            ? error
            : error instanceof Error
            ? error.message
            : t("admin.packs.errorLoadDescription"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast, t, page]);

  useEffect(() => {
    void fetchPacks();
  }, [fetchPacks]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);


  const handleOpenCreate = () => {
    setSelectedPack(null);
    setModalOpen(true);
  };

  const handleEdit = useCallback((pack: InvestmentPack) => {
    setSelectedPack(pack);
    setModalOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (pack: InvestmentPack) => {
      const packName = pickPackName(pack, i18n.language);
      const ok = window.confirm(
        t("admin.packs.deleteConfirm", {
          packName,
        })
      );
      if (!ok) {
        return;
      }
      try {
        setDeletingId(pack.id);
        await packService.deletePack(pack.id);
        toast({
          title: t("admin.packs.deleteSuccessTitle"),
          description: t("admin.packs.deleteSuccessDescription", {
            packName,
          }),
          variant: "success",
        });
        await fetchPacks();
      } catch (error) {
        toast({
          title: t("admin.packs.errorTitle"),
          description:
            typeof error === "string"
              ? error
              : error instanceof Error
              ? error.message
              : t("admin.packs.deleteErrorDescription"),
          variant: "destructive",
        });
      } finally {
        setDeletingId(null);
      }
    },
    [toast, t, i18n.language, fetchPacks]
  );

  const handleSubmit = useCallback(
    async (payload: CreatePackPayload | UpdatePackPayload, isEdit: boolean) => {
      try {
        setSaving(true);
        if (isEdit && selectedPack) {
          await packService.updatePack(
            selectedPack.id,
            payload as UpdatePackPayload
          );
          toast({
            title: t("admin.packs.updateSuccessTitle"),
            description: t("admin.packs.updateSuccessDescription"),
            variant: "success",
          });
        } else {
          await packService.createPack(payload as CreatePackPayload);
          toast({
            title: t("admin.packs.createSuccessTitle"),
            description: t("admin.packs.createSuccessDescription"),
            variant: "success",
          });
        }
        setModalOpen(false);
        setSelectedPack(null);
        await fetchPacks();
      } catch (error) {
        let description: string;
        if (typeof error === "string") {
          // Message spécifique renvoyé par le backend pour les doublons d'allocations
          if (
            error.includes(
              "Chaque projet ne peut être sélectionné qu'une seule fois"
            )
          ) {
            description = t("admin.packs.duplicateAllocationError");
          } else {
            description = error;
          }
        } else if (error instanceof Error) {
          description = error.message;
        } else {
          description = isEdit
            ? t("admin.packs.updateErrorDescription")
            : t("admin.packs.createErrorDescription");
        }

        toast({
          title: t("admin.packs.errorTitle"),
          description,
          variant: "destructive",
        });
      } finally {
        setSaving(false);
      }
    },
    [selectedPack, toast, t, fetchPacks]
  );

  const columns: DataTableColumn<InvestmentPack>[] = useMemo(
    () => [
      {
        key: "name",
        header: t("admin.packs.tableName"),
        cell: (pack: InvestmentPack) => {
          return (
            <div className="font-semibold text-foreground dark:text-white">
              {pickPackName(pack, i18n.language)}
            </div>
          );
        },
      },
      {
        key: "totalAmount",
        header: t("admin.packs.tableAmount"),
        cell: (pack: InvestmentPack) => (
          <span className="text-foreground dark:text-white">
            ${pack.totalAmount.toLocaleString()}
          </span>
        ),
      },
      {
        key: "purchases",
        header: t("admin.packs.tablePurchases"),
        cell: (pack: InvestmentPack) => (
          <span className="text-muted-foreground dark:text-white/80">
            {pack.currentPurchases}
            {pack.maxPurchases !== null && ` / ${pack.maxPurchases}`}
          </span>
        ),
      },
      {
        key: "actions",
        header: "",
        cell: (pack: InvestmentPack) => (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleEdit(pack)}
              className="text-muted-foreground hover:text-foreground dark:text-white/70 dark:hover:text-white">
              {t("admin.packs.editButton")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(pack)}
              disabled={deletingId === pack.id}
              className="text-red-400 hover:text-red-300">
              {deletingId === pack.id
                ? t("admin.packs.deleting")
                : t("admin.packs.deleteButton")}
            </Button>
          </div>
        ),
      },
    ],
    [t, i18n.language, handleEdit, handleDelete, deletingId]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-semibold text-foreground dark:text-white">
            {t("admin.packs.title")}
          </h2>
          <p className="text-sm text-muted-foreground dark:text-white/70">{t("admin.packs.subtitle")}</p>
        </div>
        <Button onClick={handleOpenCreate}>{t("admin.packs.newButton")}</Button>
      </div>

      <DataTable
        columns={columns}
        data={packs}
        isLoading={loading}
        emptyState={t("admin.packs.empty")}
      />

      {/* Pagination */}
      {packs.length > 0 && totalPages > 1 && (
        <div className="mt-4 border-t border-border pt-3 dark:border-white/10">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalPacks}
            itemsPerPage={packsPerPage}
            onPageChange={setPage}
            isLoading={loading}
            ariaLabelPrevious={t("admin.pagination.previous")}
            ariaLabelNext={t("admin.pagination.next")}
          />
        </div>
      )}

      <PackFormModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedPack(null);
        }}
        onSubmit={handleSubmit}
        isSubmitting={saving}
        pack={selectedPack}
      />
    </div>
  );
};

export default AdminInvestmentPacksPage;
